import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { MechanicalChecklistDisplay, calculateAverageScore, getOverallCondition } from "@/components/MechanicalChecklist";
import { PhotoGalleryViewer } from "@/components/PhotoGalleryViewer";
import { scooterApi, formatPrice, getFileUrl } from "@/lib/api";
import { 
  Gauge,
  Calendar,
  Fuel,
  Palette,
  Hash,
  Settings,
  Wrench,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  MessageCircle,
  Phone,
  Mail,
  Eye,
  Copy,
  Instagram,
  Share2,
  ShieldCheck,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";

// Contact info
const CONTACT_INFO = {
  phone: "0792250058",
  whatsapp: "40792250058",
  email: "office@scutersh.com"
};

export default function PublicScooterPage() {
  const { scooterId } = useParams();
  const [scooter, setScooter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);

  useEffect(() => {
    fetchScooter();
  }, [scooterId]);

  const fetchScooter = async () => {
    try {
      const data = await scooterApi.getPublic(scooterId);
      setScooter(data);
    } catch (err) {
      setError("Scooter not found");
    } finally {
      setLoading(false);
    }
  };

  const generateWhatsAppLink = () => {
    if (!scooter) return '#';
    const scooterUrl = window.location.href;
    const message = `Bună! Sunt interesat de scuterul ${scooter.brand} ${scooter.model} ${scooter.year} (${formatPrice(scooter.selling_price)}).

Link: ${scooterUrl}

Aș dori mai multe informații. Mulțumesc!`;
    return `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(message)}`;
  };

  const defaultImage = "https://images.unsplash.com/photo-1608551883326-2c9bd5d52618?w=800&h=600&fit=crop";

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary font-mono">Loading...</div>
      </div>
    );
  }

  if (error || !scooter) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mb-4">
          <span className="font-heading font-black text-white text-2xl">S</span>
        </div>
        <h1 className="font-heading font-bold text-xl mb-2">Scooter Not Found</h1>
        <p className="text-muted-foreground text-center mb-4">
          The scooter you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/showroom">
          <Button variant="outline">Vezi toate scuterele</Button>
        </Link>
      </div>
    );
  }

  const photos = scooter.photos?.length > 0 
    ? scooter.photos.map(p => getFileUrl(p))
    : [defaultImage];

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const openGallery = (index = 0) => {
    setCurrentPhotoIndex(index);
    setGalleryOpen(true);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="public-scooter-page">
      <div className="noise-overlay" />
      
      {/* Full-screen Photo Gallery Viewer */}
      <PhotoGalleryViewer
        photos={photos}
        initialIndex={currentPhotoIndex}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
      />
      
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/showroom" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
              <span className="font-heading font-black text-white text-lg">S</span>
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg leading-tight">SCUTERSH</h1>
              <p className="text-xs text-muted-foreground font-mono">Premium Scooters</p>
            </div>
          </Link>
          
          {/* View count badge */}
          {scooter.view_count > 0 && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {scooter.view_count} vizualizări
            </Badge>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 animate-fade-in">
        {/* Hero Section */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="font-mono text-sm text-primary">{scooter.scooter_id}</span>
            <StatusBadge status={scooter.status} />
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl mb-2">
            {scooter.brand} {scooter.model}
          </h1>
          <p className="text-muted-foreground font-mono">
            {scooter.year} • {scooter.engine_cc}cc • {scooter.color}
          </p>
        </div>

        {/* Photo Gallery - Clickable for zoom */}
        <Card className="border-border mb-6 overflow-hidden">
          <div 
            className="relative aspect-video cursor-pointer group"
            onClick={() => openGallery(currentPhotoIndex)}
          >
            <img
              src={photos[currentPhotoIndex]}
              alt={`${scooter.brand} ${scooter.model}`}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            
            {/* Zoom indicator */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 rounded-full p-3">
                <ZoomIn className="w-8 h-8 text-white" />
              </div>
            </div>
            
            {/* Photo count */}
            <div className="absolute top-3 right-3 bg-black/60 rounded-full px-3 py-1 text-white text-sm font-mono">
              {currentPhotoIndex + 1} / {photos.length}
            </div>
            
            {/* Click hint */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 rounded-full px-3 py-1 text-white text-xs">
              Cliquez pour agrandir / Click to zoom
            </div>
            
            {photos.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </>
            )}
          </div>
          
          {/* Thumbnail Strip */}
          {photos.length > 1 && (
            <div className="p-3 bg-secondary/50 overflow-x-auto">
              <div className="flex gap-2">
                {photos.map((photo, idx) => (
                  <button
                    key={idx}
                    onClick={() => openGallery(idx)}
                    className={`w-16 h-16 rounded overflow-hidden border-2 transition-all flex-shrink-0 ${
                      idx === currentPhotoIndex ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={photo}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* WhatsApp Contact Button - Sticky on Mobile */}
        <div className="mb-6">
          <a href={generateWhatsAppLink()} target="_blank" rel="noopener noreferrer">
            <Button className="w-full bg-green-500 hover:bg-green-600 text-white text-lg py-6">
              <MessageCircle className="w-6 h-6 mr-3" />
              Contactează pe WhatsApp
            </Button>
          </a>
          <p className="text-xs text-muted-foreground text-center mt-2">
            Răspundem în câteva minute!
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Price & Key Info */}
          <Card className="border-border bg-gradient-to-br from-card to-card/50">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">
                Price / Preț
              </p>
              <p className="font-mono font-bold text-4xl text-primary mb-6">
                {formatPrice(scooter.selling_price)}
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Gauge className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Kilometraj</span>
                  <span className="ml-auto font-mono">{scooter.mileage.toLocaleString()} km</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">An fabricație</span>
                  <span className="ml-auto font-mono">{scooter.year}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Fuel className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Motor</span>
                  <span className="ml-auto font-mono">{scooter.engine_cc}cc</span>
                </div>
                <div className="flex items-center gap-3">
                  <Palette className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Culoare</span>
                  <span className="ml-auto font-mono">{scooter.color}</span>
                </div>
                <Separator />
                <div className="flex items-center gap-3">
                  <Hash className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">VIN</span>
                  <span className="ml-auto font-mono text-xs">{scooter.vin}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card className="border-border border-green-500/30">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <MessageCircle className="w-5 h-5 text-green-500" />
                Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <a 
                href={`tel:${CONTACT_INFO.phone}`}
                className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
              >
                <Phone className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-mono font-bold">{CONTACT_INFO.phone}</p>
                  <p className="text-xs text-muted-foreground">Apelează direct</p>
                </div>
              </a>
              
              <a 
                href={generateWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg hover:bg-green-500/20 transition-colors"
              >
                <MessageCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-bold text-green-500">WhatsApp</p>
                  <p className="text-xs text-muted-foreground">Răspuns rapid</p>
                </div>
              </a>
              
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
              >
                <Mail className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-bold">{CONTACT_INFO.email}</p>
                  <p className="text-xs text-muted-foreground">Trimite email</p>
                </div>
              </a>
            </CardContent>
          </Card>

          {/* Technical Specs */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings className="w-5 h-5 text-primary" />
                Fișă Tehnică
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(scooter.technical_file || {}).map(([key, value]) => (
                value && (
                  <div key={key} className="flex justify-between">
                    <span className="text-sm text-muted-foreground capitalize">
                      {key.replace(/_/g, " ")}
                    </span>
                    <span className="font-mono text-sm">{value}</span>
                  </div>
                )
              ))}
              {!Object.values(scooter.technical_file || {}).some(v => v) && (
                <p className="text-muted-foreground text-sm text-center py-4">
                  Date tehnice indisponibile
                </p>
              )}
            </CardContent>
          </Card>

          {/* Mechanical Status */}
          <Card className="border-border md:col-span-2">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Wrench className="w-5 h-5 text-primary" />
                  Stare Tehnică
                </CardTitle>
                {scooter.mechanical_check && (
                  <div className="text-right">
                    <span className={`font-mono font-bold text-lg ${getOverallCondition(calculateAverageScore(scooter.mechanical_check)).color}`}>
                      {calculateAverageScore(scooter.mechanical_check)}/5
                    </span>
                    <span className="text-xs text-muted-foreground ml-2">
                      {getOverallCondition(calculateAverageScore(scooter.mechanical_check)).label}
                    </span>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Trust Banner */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-green-500" />
                    <span className="text-sm font-bold text-green-500">Verificat Mecanic Scutersh</span>
                  </div>
                  {scooter.mechanical_check?.aprobat_utilizare !== false && (
                    <span className="flex items-center gap-1 text-green-500 text-xs"><CheckCircle className="w-3 h-3" /> Aprobat</span>
                  )}
                </div>
              </div>

              {/* Mechanic Photos */}
              {(() => {
                const mc = scooter.mechanical_check || {};
                const mechPhotos = Object.entries(mc).filter(([k, v]) => k.endsWith('_photos') && Array.isArray(v) && v.length > 0);
                if (mechPhotos.length === 0) return null;
                const allPhotos = mechPhotos.flatMap(([, v]) => v);
                return (
                  <div className="mb-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Fotografii verificare</p>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {allPhotos.slice(0, 8).map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noreferrer" className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border border-border hover:border-primary/50 transition-colors">
                          <img src={url} alt="" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Recommendations */}
              {scooter.mechanical_check?.recomandari_mecanic && (
                <div className="mb-4 p-2 bg-secondary/50 rounded text-sm">
                  <p className="text-xs text-muted-foreground mb-1">Recomandari mecanic:</p>
                  <p>{scooter.mechanical_check.recomandari_mecanic}</p>
                </div>
              )}

              <MechanicalChecklistDisplay values={scooter.mechanical_check} />
            </CardContent>
          </Card>
        </div>

        {/* Why Buy From Us */}
        <Card className="border-border border-primary/30 mt-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">De ce să cumperi de la noi?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <span className="text-green-500 font-bold">[+]</span>
              <p className="text-sm">
                <strong>Transparență totală</strong> - Fiecare scuter are un dosar digital complet cu istoricul reparațiilor și verificările tehnice
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-green-500 font-bold">[+]</span>
              <p className="text-sm">
                <strong>Ne ocupăm de toate reparațiile</strong> - Scuterele sunt verificate și pregătite pentru utilizare
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-green-500 font-bold">[+]</span>
              <p className="text-sm">
                <strong>Calitate garantată</strong> - Selectăm doar scutere în stare bună cu consultanță post-vânzare
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Share Buttons */}
        <Card className="border-border mb-6 bg-emerald-500/5 border-emerald-500/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2"><Share2 className="w-5 h-5" /> Partajeaza</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="text-sm" onClick={() => {
              const boutiqueUrl = `${window.location.origin}/boutique`;
              const mc = scooter.mechanical_check;
              const score = mc?.scor_general || '';
              const noteText = score ? `Nota tehnica: ${score}/5\n` : '';
              const text = `${scooter.brand} ${scooter.model} ${scooter.year}\n${scooter.engine_cc}cc | ${scooter.mileage?.toLocaleString()} km | ${scooter.color}\nPret: ${scooter.selling_price} EUR\n${noteText}\nVerificat tehnic. Garantie inclusa.\n\nVrei alte scutere sau piese? Pe magazinul nostru gasesti scutere, piese de schimb si scutere pentru dezmembrare!\nNu gasesti ce cauti? Cere-ne si iti oferim cel mai bun pret!\nLivram in toata Romania.\n\n${boutiqueUrl}\n\noffice@scutersh.ro | 0792250058\n\n#scutersh #scuter #${scooter.brand?.toLowerCase()?.replace(/\s/g,'')} #${scooter.model?.toLowerCase()?.replace(/\s/g,'')} #scutersecondhand #piese #dezmembrari #romania`;
              navigator.clipboard.writeText(text);
              toast.success("Text Instagram copiat!");
            }} data-testid="share-instagram">
              <Instagram className="w-4 h-4 mr-1.5" /> Copie Instagram
            </Button>
            <Button variant="outline" size="sm" className="text-sm" onClick={() => {
              const boutiqueUrl = `${window.location.origin}/boutique`;
              const mc = scooter.mechanical_check;
              const score = mc?.scor_general || '';
              const noteText = score ? `- Nota tehnica: ${score}/5\n` : '';
              const text = `${scooter.brand} ${scooter.model} ${scooter.year} - ${scooter.selling_price} EUR\n\nCaracteristici:\n- Motor: ${scooter.engine_cc}cc\n- Kilometraj: ${scooter.mileage?.toLocaleString()} km\n- Culoare: ${scooter.color}\n- Stare: Verificat tehnic\n${noteText}${scooter.repairs_done ? `- Reparatii: ${scooter.repairs_done}\n` : ''}${scooter.warranty_info ? `- Garantie: ${scooter.warranty_info}\n` : ''}\nScuter verificat si pregatit pentru utilizare.\n\nMagazin online cu scutere, piese de schimb si dezmembrari:\n${boutiqueUrl}\n\nNu gasesti ce cauti? Contacteaza-ne si iti oferim cel mai bun pret!\nLivram in toata Romania.\n\nContact: office@scutersh.ro | 0792250058 | WhatsApp: +40792250058`;
              navigator.clipboard.writeText(text);
              toast.success("Text OLX copiat!");
            }} data-testid="share-olx">
              <Copy className="w-4 h-4 mr-1.5" /> Copie OLX
            </Button>
            <Button variant="outline" size="sm" className="text-sm" onClick={() => {
              const shareUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/share/scooter/${scooter.scooter_id}`;
              navigator.clipboard.writeText(shareUrl);
              toast.success("Lien Facebook copiat!");
            }} data-testid="share-facebook">
              <Share2 className="w-4 h-4 mr-1.5" /> Copie Facebook
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-12 text-center">
          <Link to="/showroom">
            <Button variant="outline" className="mb-6">
              Vezi toate scuterele disponibile
            </Button>
          </Link>
          <Separator className="my-6" />
          <p className="text-xs text-muted-foreground font-mono">
            © {new Date().getFullYear()} Scutersh • Scutere & Piese Premium
          </p>
        </div>
      </main>

      {/* Floating WhatsApp Button for Mobile */}
      <div className="fixed bottom-6 right-6 z-40 md:hidden">
        <a href={generateWhatsAppLink()} target="_blank" rel="noopener noreferrer">
          <Button className="w-14 h-14 rounded-full bg-green-500 hover:bg-green-600 shadow-lg">
            <MessageCircle className="w-7 h-7" />
          </Button>
        </a>
      </div>
    </div>
  );
}
